import React, { type ErrorInfo } from 'react';
import { DefaultFallback } from './DefaultFallback';
import { useAwsRum } from '@/common';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error?: Error; resetError?: () => void }>;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    componentName?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

/**
 * Implements a custom error boundary component that will catch errors in the app and display a fallback UI
 */
export class ErrorBoundaryComponent extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log the error
        console.error(
            `ErrorBoundary caught an error${this.props.componentName ? ` in ${this.props.componentName}` : ''}:`,
            error,
            errorInfo,
        );

        // Call the onError callback if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || DefaultFallback;
            return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
        }

        return this.props.children;
    }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, fallback, onError }) => {
    const awsRum = useAwsRum();

    const handleError = (error: Error, errorInfo: ErrorInfo) => {
        if (awsRum) {
            awsRum.recordError(error);
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const DD_RUM = (window as any).DD_RUM;
            if (DD_RUM) {
                DD_RUM.addError(error, {
                    componentStack: errorInfo.componentStack,
                    errorBoundary: true,
                });
            }
        } catch (ddError) {
            console.error('Failed to report error to DataDog:', ddError);
        }

        if (onError) {
            onError(error, errorInfo);
        }
    };
    return (
        <ErrorBoundaryComponent onError={handleError} fallback={fallback}>
            {children}
        </ErrorBoundaryComponent>
    );
};
